// RD-163: редактор раздела «Настройки» — загрузка блоков, локальные черновики, diff текущей
// области, сохранение и навигация с dirty guard. Создаётся в Settings.vue и передаётся
// дочерним компонентам через provide/inject; общий store получает только подтверждённые данные.

import { computed, inject, nextTick, reactive, ref, type InjectionKey } from 'vue'
import { useSettingsStore } from '@/stores/settingsStore'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useAuthStore } from '@/stores/authStore'
import type { Option, Position, Role } from '@/types'
import {
    EXCHANGE_DELAY_FIELDS,
    EXCHANGE_GLOBAL_KEYS,
    EXCHANGE_RULE_FIELDS,
    GENERAL_GLOBAL_KEYS,
    LUNCH_RULES_LIMIT,
    STATISTIC_FIELDS,
    TIME_FIELDS,
    boolText,
    globalChange,
    isShortNameChanged,
    lunchChanges,
    lunchDraftFromRules,
    lunchRowErrors,
    normalizeRole,
    prunePositionDraft,
    pruneGlobalDraft,
    pruneRoleDraft,
    reconcileLunchDraft,
    revertLunchChange,
    revertRoleChange,
    roleChanges,
    setRolePermissions,
    sortLunchDraftRows,
    sortLunchRules,
    type GlobalDraft,
    type GlobalKey,
    type LunchDraft,
    type PositionDraft,
    type PositionValues,
    type RoleBooleanKey,
    type RoleDraft,
    type RoleValues,
    type SettingsChange,
    type SettingsSectionId,
    type StatisticsDraft,
    type StatisticsKey,
    ROLE_DAYS_KEY,
} from './settingsModel'
import {
    saveGlobalPart,
    saveLunches,
    savePositions,
    saveRole,
    saveStatistics,
    type GlobalState,
    type LunchState,
    type PositionsState,
    type RoleState,
    type SaveOutcome,
    type StatisticsState,
    type SettingsApi,
} from './settingsSave'
import { settingsApi } from './settingsApi'
import type { SettingsNavTarget } from './settingsSearch'

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error'
export type RoleFilter = 'all' | 'on' | 'off' | 'changed'

export interface ChangeGroup {
    block: string
    items: SettingsChange[]
}

let localKeySeed = 0
const nextLocalKey = () => `new-${Date.now().toString(36)}-${++localKeySeed}`

export function createSettingsEditor(api: SettingsApi = settingsApi) {
    const settingsStore = useSettingsStore()
    const scheduleStore = useScheduleStore()
    const authStore = useAuthStore()

    /* ---------- Доступ: прежние gates вкладок ---------- */
    const canEditRoles = computed(() => Boolean(authStore.permissions?.can_edit_roles))
    const canManageExchange = computed(() => Boolean(authStore.permissions?.can_manage_shift_exchange))
    const canEditPositions = computed(() => Boolean(authStore.permissions?.can_edit_position))

    const sectionEnabled = (section: SettingsSectionId) => {
        if (section === 'roles') return canEditRoles.value
        if (section === 'exchange') return canManageExchange.value
        return true
    }
    const availableSections = computed(() => (['general', 'roles', 'positions', 'exchange'] as SettingsSectionId[]).filter(sectionEnabled))

    /* ---------- Загрузка ---------- */
    const load = reactive<Record<'global' | 'statistics' | 'lunches' | 'roles' | 'positions', LoadStatus>>({
        global: 'idle',
        statistics: 'idle',
        lunches: 'idle',
        roles: 'idle',
        positions: 'idle',
    })

    const globalDraft = ref<GlobalDraft>({})
    const statisticsDraft = ref<StatisticsDraft>({})
    const lunchDraft = ref<LunchDraft>({ rows: [], deleted: [] })
    const roleDrafts = ref<Record<number, RoleDraft>>({})
    const positions = ref<Position[]>([])
    const positionBaselines = ref<Record<string, PositionValues>>({})
    const positionDrafts = ref<Record<string, PositionDraft>>({})
    const shiftSchedules = ref<Option[]>([])
    const globalNeedsReload = ref(false)
    const lunchBlocked = ref<null | 'reload' | 'verify'>(null)

    async function loadGlobal() {
        load.global = 'loading'
        const error = await settingsStore.fetchSettings()
        if (error || !settingsStore.settings) {
            load.global = 'error'
            return
        }
        globalNeedsReload.value = false
        globalDraft.value = pruneGlobalDraft(settingsStore.settings, globalDraft.value)
        load.global = 'ready'
    }

    async function loadStatistics() {
        load.statistics = 'loading'
        const error = await settingsStore.fetchEmployeeStatisticsSettings()
        if (error || !settingsStore.employeeStatisticsSettings) {
            load.statistics = 'error'
            return
        }
        load.statistics = 'ready'
    }

    async function loadLunches() {
        const previous = load.lunches === 'ready' ? [...settingsStore.lunchSettings] : null
        load.lunches = 'loading'
        const error = await settingsStore.fetchLunchSettings()
        if (error) {
            load.lunches = previous ? 'ready' : 'error'
            if (previous) settingsStore.lunchSettings = previous
            return false
        }
        // Повторная загрузка не стирает черновик: правки сверяются по ID.
        if (previous) {
            const wasBlocked = lunchBlocked.value === 'reload'
            const { draft, newServerIds } = reconcileLunchDraft(previous, settingsStore.lunchSettings, lunchDraft.value)
            lunchDraft.value = draft
            if (wasBlocked) {
                // После неудачного чтения результат записи был неизвестен: новые правила на сервере
                // при несозданных строках черновика нельзя надёжно сопоставить — нужна сверка.
                const hasPendingCreates = draft.rows.some((row) => row.id === null)
                lunchBlocked.value = newServerIds.length && hasPendingCreates ? 'verify' : null
            }
        } else {
            lunchDraft.value = lunchDraftFromRules(settingsStore.lunchSettings)
        }
        load.lunches = 'ready'
        return true
    }

    async function loadRoles() {
        if (!canEditRoles.value) return
        load.roles = 'loading'
        const error = await settingsStore.fetchRoles()
        if (error) {
            load.roles = 'error'
            return
        }
        if (selectedRoleId.value === null || !settingsStore.roles.some((role) => role.id === selectedRoleId.value)) {
            selectedRoleId.value = settingsStore.roles[0]?.id ?? null
        }
        load.roles = 'ready'
    }

    async function loadPositions() {
        load.positions = 'loading'
        try {
            const [loadedPositions, loadedSchedules] = await Promise.all([
                scheduleStore.fetchPositions(),
                scheduleStore.fetchShiftSchedules(),
            ])
            if (!Array.isArray(loadedPositions) || !Array.isArray(loadedSchedules)) {
                console.error('Получены некорректные данные должностей или графиков')
                load.positions = 'error'
                return
            }
            positions.value = loadedPositions.map((position: Position) => ({ ...position }))
            positionBaselines.value = Object.fromEntries(positions.value.map((position) => [
                position.id,
                { short_name: position.short_name ?? null, shift_schedule: position.shift_schedule ?? null },
            ]))
            const drafts: Record<string, PositionDraft> = {}
            Object.entries(positionDrafts.value).forEach(([id, draft]) => {
                const baseline = positionBaselines.value[id]
                if (baseline) drafts[id] = prunePositionDraft(baseline, draft)
            })
            positionDrafts.value = drafts
            shiftSchedules.value = loadedSchedules
            load.positions = 'ready'
        } catch (error) {
            console.error('Не удалось загрузить должности или графики', error)
            load.positions = 'error'
        }
    }

    async function loadAll() {
        await Promise.all([
            loadGlobal(),
            loadStatistics(),
            loadLunches(),
            loadRoles(),
            loadPositions(),
        ])
    }

    /* ---------- Навигация ---------- */
    const section = ref<SettingsSectionId>('general')
    const selectedRoleId = ref<number | null>(null)
    const activeAnchor = ref<string | null>(null)
    const highlightedAnchor = ref<string | null>(null)
    const roleView = reactive({
        query: '',
        filter: 'all' as RoleFilter,
        collapsed: {} as Record<string, boolean>,
    })

    const selectedRole = computed<Role | null>(() => settingsStore.roles.find((role) => role.id === selectedRoleId.value) ?? null)

    /* ---------- Global / statistics ---------- */
    const globalReady = computed(() => load.global === 'ready' && Boolean(settingsStore.settings))

    function globalValue(key: GlobalKey): string | boolean | null {
        if (key in globalDraft.value) return globalDraft.value[key] as string | boolean
        const value = settingsStore.settings?.[key]
        if (value === undefined || value === null) return null
        return typeof value === 'boolean' ? value : String(value)
    }

    function setGlobal(key: GlobalKey, value: string | boolean) {
        if (!settingsStore.settings || saving.value) return
        globalDraft.value = pruneGlobalDraft(settingsStore.settings, { ...globalDraft.value, [key]: value })
        // Невалидный текст держим в черновике, даже если pruneGlobalDraft его не удалил.
    }

    const statisticsReady = computed(() => load.statistics === 'ready' && Boolean(settingsStore.employeeStatisticsSettings))

    function statisticValue(key: StatisticsKey): boolean {
        if (key in statisticsDraft.value) return Boolean(statisticsDraft.value[key])
        return Boolean(settingsStore.employeeStatisticsSettings?.[key])
    }

    function setStatistic(key: StatisticsKey, value: boolean) {
        const baseline = settingsStore.employeeStatisticsSettings
        if (!baseline || saving.value) return
        const next = { ...statisticsDraft.value, [key]: value }
        if (baseline[key] === value) delete next[key]
        statisticsDraft.value = next
    }

    /* ---------- Обеды ---------- */
    const lunchesReady = computed(() => load.lunches === 'ready')
    const lunchErrors = computed(() => lunchRowErrors(lunchDraft.value.rows))
    const canAddLunch = computed(() => lunchesReady.value && !saving.value && lunchDraft.value.rows.length < LUNCH_RULES_LIMIT)

    function addLunchRow() {
        if (!canAddLunch.value) return
        const rows = lunchDraft.value.rows
        const last = rows[rows.length - 1]
        const lastTo = last ? Number(last.to) : NaN
        lunchDraft.value = {
            ...lunchDraft.value,
            rows: [...rows, { key: nextLocalKey(), id: null, to: Number.isFinite(lastTo) && last ? String(lastTo + 60) : '', brk: '' }],
        }
    }

    function updateLunchRow(key: string, field: 'to' | 'brk', value: string) {
        if (saving.value) return
        lunchDraft.value = {
            ...lunchDraft.value,
            rows: lunchDraft.value.rows.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
        }
    }

    /** Упорядочить строки после ввода границы (по blur), чтобы «от» соответствовало соседям. */
    function sortLunchRows() {
        lunchDraft.value = { ...lunchDraft.value, rows: sortLunchDraftRows(lunchDraft.value.rows) }
    }

    function removeLunchRow(key: string) {
        if (saving.value) return
        const row = lunchDraft.value.rows.find((item) => item.key === key)
        if (!row) return
        lunchDraft.value = {
            rows: lunchDraft.value.rows.filter((item) => item.key !== key),
            deleted: row.id ? [...lunchDraft.value.deleted, row.id] : [...lunchDraft.value.deleted],
        }
    }

    function confirmLunchVerification() {
        if (lunchBlocked.value === 'verify') lunchBlocked.value = null
    }

    /* ---------- Роли ---------- */
    const roleBaselines = computed<Record<number, RoleValues>>(() =>
        Object.fromEntries(settingsStore.roles.map((role) => [role.id, normalizeRole(role)])))

    const roleDraft = (roleId: number): RoleDraft => roleDrafts.value[roleId] ?? {}

    function roleValue(roleId: number, key: RoleBooleanKey): boolean {
        const draft = roleDraft(roleId)
        return key in draft ? Boolean(draft[key]) : Boolean(roleBaselines.value[roleId]?.[key])
    }

    function roleDaysText(roleId: number): string {
        const draft = roleDraft(roleId)
        if (ROLE_DAYS_KEY in draft) return draft[ROLE_DAYS_KEY] ?? ''
        const value = roleBaselines.value[roleId]?.[ROLE_DAYS_KEY]
        return value === null || value === undefined ? '' : String(value)
    }

    function setRolePermissionValues(roleId: number, updates: Partial<Record<RoleBooleanKey, boolean>>) {
        const baseline = roleBaselines.value[roleId]
        if (!baseline || saving.value) return
        roleDrafts.value = { ...roleDrafts.value, [roleId]: setRolePermissions(baseline, roleDraft(roleId), updates) }
    }

    function setRoleDays(roleId: number, text: string) {
        const baseline = roleBaselines.value[roleId]
        if (!baseline || saving.value) return
        roleDrafts.value = { ...roleDrafts.value, [roleId]: pruneRoleDraft(baseline, { ...roleDraft(roleId), [ROLE_DAYS_KEY]: text }) }
    }

    /* ---------- Должности ---------- */
    function positionValue(id: string): PositionValues {
        const baseline = positionBaselines.value[id] ?? { short_name: null, shift_schedule: null }
        return { ...baseline, ...(positionDrafts.value[id] ?? {}) }
    }

    function setPosition(id: string, field: keyof PositionValues, value: string | null) {
        const baseline = positionBaselines.value[id]
        if (!baseline || saving.value || !canEditPositions.value) return
        positionDrafts.value = { ...positionDrafts.value, [id]: prunePositionDraft(baseline, { ...(positionDrafts.value[id] ?? {}), [field]: value }) }
    }

    const scheduleName = (id: string | null) => shiftSchedules.value.find((schedule) => schedule.id === id)?.name ?? null

    /* ---------- Изменения по областям ---------- */
    const generalChanges = computed<SettingsChange[]>(() => {
        const changes: SettingsChange[] = []
        const baseline = settingsStore.settings
        if (baseline && globalReady.value) {
            TIME_FIELDS.forEach((field) => {
                const change = globalChange(baseline, globalDraft.value, field.key, 'Учёт времени', field.label, field.unit)
                if (change) changes.push(change)
            })
        }
        const statistics = settingsStore.employeeStatisticsSettings
        if (statistics && statisticsReady.value) {
            STATISTIC_FIELDS.forEach((field) => {
                if (!(field.key in statisticsDraft.value) || statisticsDraft.value[field.key] === statistics[field.key]) return
                changes.push({
                    id: `stats:${field.key}`,
                    block: 'Показатели сотрудника',
                    label: `${field.code} — ${field.label}`,
                    before: boolText(statistics[field.key]),
                    after: boolText(statisticsDraft.value[field.key]),
                    anchor: `setting-${field.key}`,
                })
            })
        }
        if (lunchesReady.value) changes.push(...lunchChanges(settingsStore.lunchSettings, lunchDraft.value))
        return changes
    })

    const exchangeChanges = computed<SettingsChange[]>(() => {
        const baseline = settingsStore.settings
        if (!baseline || !globalReady.value) return []
        const changes: SettingsChange[] = []
        EXCHANGE_DELAY_FIELDS.forEach((field) => {
            const change = globalChange(baseline, globalDraft.value, field.key, 'Доступность вакансии', field.label, field.unit)
            if (change) changes.push(change)
        })
        EXCHANGE_RULE_FIELDS.forEach((field) => {
            const change = globalChange(baseline, globalDraft.value, field.key, 'Правила подбора', field.label)
            if (change) changes.push(change)
        })
        return changes
    })

    const positionChanges = computed<SettingsChange[]>(() => {
        const changes: SettingsChange[] = []
        positions.value.forEach((position) => {
            const baseline = positionBaselines.value[position.id]
            const draft = positionDrafts.value[position.id]
            if (!baseline || !draft) return
            if ('short_name' in draft && isShortNameChanged(draft.short_name, baseline.short_name)) {
                changes.push({
                    id: `position:${position.id}:short_name`,
                    block: 'Должности',
                    label: position.name,
                    before: `Короткое: ${baseline.short_name || 'пусто'}`,
                    after: draft.short_name || 'пусто',
                    anchor: `position-${position.id}`,
                })
            }
            if ('shift_schedule' in draft && draft.shift_schedule !== baseline.shift_schedule) {
                changes.push({
                    id: `position:${position.id}:shift_schedule`,
                    block: 'Должности',
                    label: position.name,
                    before: `График: ${scheduleName(baseline.shift_schedule) ?? 'не выбран'}`,
                    after: scheduleName(draft.shift_schedule ?? null) ?? 'не выбран',
                    anchor: `position-${position.id}`,
                })
            }
        })
        return changes
    })

    function roleChangeList(roleId: number): SettingsChange[] {
        const baseline = roleBaselines.value[roleId]
        return baseline ? roleChanges(baseline, roleDraft(roleId)) : []
    }

    const rolesChangeCount = computed(() => settingsStore.roles.reduce((sum, role) => sum + roleChangeList(role.id).length, 0))

    type AreaKey = string
    const areaOf = (target: { section: SettingsSectionId, roleId?: number | null }): AreaKey =>
        target.section === 'roles' ? `role:${target.roleId ?? ''}` : target.section

    const currentArea = computed<AreaKey>(() => areaOf({ section: section.value, roleId: selectedRoleId.value }))

    function areaChanges(area: AreaKey): SettingsChange[] {
        if (area === 'general') return generalChanges.value
        if (area === 'exchange') return exchangeChanges.value
        if (area === 'positions') return positionChanges.value
        const roleId = Number(area.replace('role:', ''))
        return Number.isFinite(roleId) ? roleChangeList(roleId) : []
    }

    const currentChanges = computed(() => areaChanges(currentArea.value))
    const currentChangeGroups = computed<ChangeGroup[]>(() => {
        const groups: ChangeGroup[] = []
        currentChanges.value.forEach((change) => {
            const group = groups.find((item) => item.block === change.block)
            if (group) group.items.push(change)
            else groups.push({ block: change.block, items: [change] })
        })
        return groups
    })

    const sectionChangeCount = (target: SettingsSectionId) => {
        if (target === 'roles') return rolesChangeCount.value
        return areaChanges(target).length
    }
    const blockHasChanges = (block: string) => generalChanges.value.some((change) => change.block === block)
        || exchangeChanges.value.some((change) => change.block === block)
    const changedAnchors = computed(() => new Set(currentChanges.value.map((change) => change.anchor).filter(Boolean) as string[]))

    const hasAnyChanges = computed(() => generalChanges.value.length > 0
        || exchangeChanges.value.length > 0
        || positionChanges.value.length > 0
        || rolesChangeCount.value > 0)

    /* ---------- Отмена ---------- */
    function revertChange(change: SettingsChange) {
        if (saving.value || change.revertBlockedReason) return
        const [kind] = change.id.split(':')
        if (kind === 'global') {
            const key = change.id.slice('global:'.length) as GlobalKey
            const next = { ...globalDraft.value }
            delete next[key]
            globalDraft.value = next
        } else if (kind === 'stats') {
            const next = { ...statisticsDraft.value }
            delete next[change.id.slice('stats:'.length) as StatisticsKey]
            statisticsDraft.value = next
        } else if (kind === 'lunch') {
            lunchDraft.value = revertLunchChange(settingsStore.lunchSettings, lunchDraft.value, change.id)
        } else if (kind === 'position') {
            const [, id, field] = change.id.split(':')
            const next = { ...(positionDrafts.value[id] ?? {}) }
            delete next[field as keyof PositionValues]
            positionDrafts.value = { ...positionDrafts.value, [id]: next }
        } else if (kind === 'role' && selectedRoleId.value !== null) {
            const roleId = selectedRoleId.value
            const baseline = roleBaselines.value[roleId]
            if (baseline) roleDrafts.value = { ...roleDrafts.value, [roleId]: revertRoleChange(baseline, roleDraft(roleId), change.id) }
        }
    }

    function resetArea(area: AreaKey) {
        if (saving.value) return
        if (area === 'general') {
            const next = { ...globalDraft.value }
            GENERAL_GLOBAL_KEYS.forEach((key) => delete next[key])
            globalDraft.value = next
            statisticsDraft.value = {}
            lunchDraft.value = lunchDraftFromRules(settingsStore.lunchSettings)
            if (lunchBlocked.value === 'verify') lunchBlocked.value = null
        } else if (area === 'exchange') {
            const next = { ...globalDraft.value }
            EXCHANGE_GLOBAL_KEYS.forEach((key) => delete next[key])
            globalDraft.value = next
        } else if (area === 'positions') {
            positionDrafts.value = {}
        } else {
            const roleId = Number(area.replace('role:', ''))
            const next = { ...roleDrafts.value }
            delete next[roleId]
            roleDrafts.value = next
        }
        delete results[area]
    }

    /* ---------- Сохранение ---------- */
    const saving = ref(false)
    const results = reactive<Record<AreaKey, SaveOutcome>>({})

    const currentInvalid = computed(() => currentChanges.value.some((change) => change.invalid))
    const canSaveCurrent = computed(() => !saving.value && currentChanges.value.length > 0 && !currentInvalid.value)

    const globalState: GlobalState = {
        get global() { return settingsStore.settings },
        set global(value) { settingsStore.settings = value },
        get globalDraft() { return globalDraft.value },
        set globalDraft(value) { globalDraft.value = value },
        get globalNeedsReload() { return globalNeedsReload.value },
        set globalNeedsReload(value) { globalNeedsReload.value = value },
    }

    const statisticsState: StatisticsState = {
        get statistics() { return statisticsReady.value ? settingsStore.employeeStatisticsSettings : null },
        set statistics(value) { settingsStore.employeeStatisticsSettings = value },
        get statisticsDraft() { return statisticsDraft.value },
        set statisticsDraft(value) { statisticsDraft.value = value },
    }

    const lunchState: LunchState = {
        get lunches() { return lunchesReady.value ? settingsStore.lunchSettings : null },
        set lunches(value) { settingsStore.lunchSettings = sortLunchRules(value ?? []) },
        get lunchDraft() { return lunchDraft.value },
        set lunchDraft(value) { lunchDraft.value = value },
        get lunchBlocked() { return lunchBlocked.value },
        set lunchBlocked(value) { lunchBlocked.value = value },
    }

    const positionsState: PositionsState = {
        get positions() { return load.positions === 'ready' ? positions.value : null },
        set positions(value) { positions.value = value ?? [] },
        get baselines() { return positionBaselines.value },
        set baselines(value) { positionBaselines.value = value },
        get drafts() { return positionDrafts.value },
        set drafts(value) { positionDrafts.value = value },
    }

    function roleState(roleId: number): RoleState {
        return {
            get role() { return settingsStore.roles.find((role) => role.id === roleId) as Role },
            set role(value) {
                const index = settingsStore.roles.findIndex((role) => role.id === roleId)
                if (index !== -1) settingsStore.roles[index] = value
            },
            get baseline() { return roleBaselines.value[roleId] },
            set baseline(_value) { /* baseline выводится из store */ },
            get draft() { return roleDraft(roleId) },
            set draft(value) { roleDrafts.value = { ...roleDrafts.value, [roleId]: value } },
        }
    }

    function syncSchedulePositions() {
        // Прежнее поведение: подтверждённые должности обновляются в scheduleStore.
        scheduleStore.positions.forEach((position, index) => {
            const baseline = positionBaselines.value[position.id]
            if (!baseline) return
            if (position.short_name !== baseline.short_name || position.shift_schedule !== baseline.shift_schedule) {
                scheduleStore.positions[index] = { ...position, ...baseline }
            }
        })
        positions.value = positions.value.map((position) => ({ ...position, ...(positionBaselines.value[position.id] ?? {}) }))
    }

    /** Сохранить область. true — всё сохранено и правок не осталось. */
    async function saveArea(area: AreaKey): Promise<boolean> {
        if (saving.value) return false
        const changes = areaChanges(area)
        if (!changes.length) return true
        if (changes.some((change) => change.invalid)) return false

        saving.value = true
        const outcome: SaveOutcome = { errors: [], warnings: [] }
        try {
            if (area === 'general') {
                await saveGlobalPart(globalState, GENERAL_GLOBAL_KEYS, 'Учёт времени', api, outcome)
                await saveStatistics(statisticsState, api, outcome)
                await saveLunches(lunchState, api, outcome)
            } else if (area === 'exchange') {
                await saveGlobalPart(globalState, EXCHANGE_GLOBAL_KEYS, 'Биржа смен', api, outcome)
            } else if (area === 'positions') {
                await savePositions(positionsState, api, outcome)
                syncSchedulePositions()
            } else {
                const roleId = Number(area.replace('role:', ''))
                if (settingsStore.roles.some((role) => role.id === roleId)) await saveRole(roleState(roleId), api, outcome)
            }
        } catch (error) {
            console.error('Сохранение настроек прервано', error)
            outcome.errors.push({ id: 'unexpected', block: 'Сохранение', message: 'Сохранение прервано непредвиденной ошибкой.' })
        } finally {
            saving.value = false
        }
        results[area] = outcome
        return !outcome.errors.length && !areaChanges(area).length
    }

    const saveCurrent = () => saveArea(currentArea.value)
    const currentResult = computed<SaveOutcome | null>(() => results[currentArea.value] ?? null)

    /* ---------- Переходы и guard ---------- */
    const guard = reactive<{ open: boolean, busy: boolean, resolve: ((proceed: boolean) => void) | null }>({ open: false, busy: false, resolve: null })

    /** Спросить «Сохранить / Не сохранять / Остаться» для текущей области. true — можно уходить. */
    function confirmLeaveCurrentArea(): Promise<boolean> {
        if (saving.value) return Promise.resolve(false)
        if (!currentChanges.value.length) return Promise.resolve(true)
        return new Promise((resolve) => {
            guard.open = true
            guard.resolve = resolve
        })
    }

    function closeGuard(proceed: boolean) {
        const resolve = guard.resolve
        guard.open = false
        guard.busy = false
        guard.resolve = null
        resolve?.(proceed)
    }

    async function guardSave() {
        guard.busy = true
        const ok = await saveCurrent()
        // Частичная ошибка оставляет пользователя в текущей области.
        closeGuard(ok)
    }

    function guardDiscard() {
        resetArea(currentArea.value)
        closeGuard(true)
    }

    const guardStay = () => closeGuard(false)

    async function navigate(target: SettingsNavTarget) {
        if (!sectionEnabled(target.section)) return
        const roleId = target.section === 'roles' ? (target.roleId ?? selectedRoleId.value) : selectedRoleId.value
        const targetArea = areaOf({ section: target.section, roleId })
        if (targetArea !== currentArea.value) {
            const proceed = await confirmLeaveCurrentArea()
            if (!proceed) return
        }
        section.value = target.section
        if (target.section === 'roles' && roleId !== null && roleId !== undefined) selectedRoleId.value = roleId
        if (target.groupId) {
            roleView.collapsed[target.groupId] = false
            roleView.filter = 'all'
            roleView.query = ''
        }
        activeAnchor.value = target.anchor ?? null
        if (target.anchor) await revealAnchor(target.anchor)
        else window.scrollTo?.({ top: 0 })
    }

    async function revealAnchor(anchor: string) {
        await nextTick()
        const element = document.getElementById(anchor)
        if (!element) return
        element.scrollIntoView({ block: 'center', behavior: 'smooth' })
        const focusable = element.matches('input, button, [tabindex]')
            ? element
            : element.querySelector<HTMLElement>('input:not([disabled]), button:not([disabled])')
        focusable?.focus({ preventScroll: true })
        highlightedAnchor.value = anchor
        window.setTimeout(() => {
            if (highlightedAnchor.value === anchor) highlightedAnchor.value = null
        }, 1600)
    }

    return {
        // доступ
        canEditRoles,
        canManageExchange,
        canEditPositions,
        availableSections,
        sectionEnabled,
        // загрузка
        load,
        loadAll,
        loadGlobal,
        loadStatistics,
        loadLunches,
        loadRoles,
        loadPositions,
        globalReady,
        statisticsReady,
        lunchesReady,
        globalNeedsReload,
        lunchBlocked,
        // навигация
        section,
        selectedRoleId,
        selectedRole,
        activeAnchor,
        highlightedAnchor,
        roleView,
        navigate,
        revealAnchor,
        // global / statistics
        globalValue,
        setGlobal,
        statisticValue,
        setStatistic,
        // обеды
        lunchDraft,
        lunchErrors,
        canAddLunch,
        addLunchRow,
        updateLunchRow,
        sortLunchRows,
        removeLunchRow,
        confirmLunchVerification,
        // роли
        roles: computed(() => settingsStore.roles),
        roleBaselines,
        roleValue,
        roleDaysText,
        setRolePermissionValues,
        setRoleDays,
        roleChangeList,
        // должности
        positions,
        shiftSchedules,
        positionValue,
        setPosition,
        // изменения
        currentArea,
        currentChanges,
        currentChangeGroups,
        currentInvalid,
        changedAnchors,
        sectionChangeCount,
        blockHasChanges,
        hasAnyChanges,
        revertChange,
        resetCurrent: () => resetArea(currentArea.value),
        resetAll: () => ['general', 'exchange', 'positions', ...settingsStore.roles.map((role) => `role:${role.id}`)].forEach(resetArea),
        // сохранение
        saving,
        canSaveCurrent,
        saveCurrent,
        currentResult,
        // guard
        guard,
        confirmLeaveCurrentArea,
        guardSave,
        guardDiscard,
        guardStay,
    }
}

export type SettingsEditor = ReturnType<typeof createSettingsEditor>

export const settingsEditorKey: InjectionKey<SettingsEditor> = Symbol('settingsEditor')

export function useSettingsEditor(): SettingsEditor {
    const editor = inject(settingsEditorKey)
    if (!editor) throw new Error('useSettingsEditor: редактор настроек не предоставлен')
    return editor
}
